import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

// The hold lasts only until graceMinutes after the invoice was created; the
// release cron sweeps unpaid widget invoices past that point. The deadline is
// derived from invoice.createdAt so it matches exactly what the server enforces.
//
// A purchase (package/voucher) reserves no slot — it is simply cancelled — so it
// must not be described as a booking whose court reopens.
export default function AutoReleaseNotice({ createdAt, graceMinutes, isSale = false, onExpire }) {
  const deadline = createdAt ? DateTime.fromISO(createdAt).plus({ minutes: graceMinutes }).toMillis() : null;
  const [remaining, setRemaining] = useState(deadline ? deadline - Date.now() : 0);

  useEffect(() => {
    if (!deadline) return undefined;
    const tick = () => setRemaining(deadline - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  const expired = !deadline || remaining <= 0;

  useEffect(() => {
    if (deadline && expired && onExpire) onExpire();
  }, [deadline, expired, onExpire]);

  const heading = expired
    ? isSale
      ? "Your purchase hold has expired"
      : "Your booking hold has expired"
    : `${isSale ? "Purchase" : "Booking"} held for ${formatCountdown(remaining)}`;

  const detail = expired
    ? isSale
      ? "Please pay now — this purchase may be cancelled at any moment if payment is not completed."
      : "Please pay now — this slot may be released at any moment if payment is not completed."
    : isSale
      ? `Complete payment within ${graceMinutes} minutes or this purchase is cancelled.`
      : `Complete payment within ${graceMinutes} minutes or this booking is released and the slot reopened.`;

  return (
    <Box
      sx={{
        mt: 3,
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "warning.light",
        color: "warning.dark"
      }}
    >
      <AccessTimeRoundedIcon fontSize="small" sx={{ mt: "1px" }} />
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {heading}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mt: 0.25 }}>
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}
